import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface DeploymentRecord {
  id: string;
  project_id: string;
  status: string;
  preview_url: string | null;
  deployment_id: string | null;
  logs: string[];
  error: string | null;
  created_at: string;
}

export function useDeploymentHistory(projectId: string | null) {
  const [deployments, setDeployments] = useState<DeploymentRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      // NOTE: deployment_history table may not exist yet - silently fail
      const { data, error } = await (supabase as any)
        .from('deployment_history')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        // Table doesn't exist or other error - just return empty array
        console.debug('[useDeploymentHistory] Table may not exist, returning empty:', error.message);
        setDeployments([]);
        return;
      }

      // Cast the data to handle the logs field properly
      const typedData = (data || []).map((d: any) => ({
        ...d,
        logs: Array.isArray(d.logs) ? d.logs : []
      })) as DeploymentRecord[];

      setDeployments(typedData);
    } catch (err) {
      // Silently fail - deployment history is non-critical
      console.debug('[useDeploymentHistory] Error fetching history:', err);
      setDeployments([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const addDeployment = useCallback(async (
    status: string,
    previewUrl?: string,
    deploymentId?: string,
    logs?: string[],
    error?: string
  ) => {
    if (!projectId) return null;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // NOTE: deployment_history table may not exist yet - silently fail
      const { data, error: insertError } = await (supabase as any)
        .from('deployment_history')
        .insert({
          user_id: user.id,
          project_id: projectId,
          status,
          preview_url: previewUrl || null,
          deployment_id: deploymentId || null,
          logs: logs || [],
          error: error || null,
        })
        .select()
        .single();

      if (insertError) {
        // Table doesn't exist - silently fail
        console.debug('[useDeploymentHistory] Could not add deployment:', insertError.message);
        return null;
      }

      // Refresh the list
      await fetchHistory();
      return data;
    } catch (err) {
      // Silently fail - deployment history is non-critical
      console.debug('[useDeploymentHistory] Error adding deployment:', err);
      return null;
    }
  }, [projectId, fetchHistory]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return {
    deployments,
    loading,
    refresh: fetchHistory,
    addDeployment,
  };
}
